"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { api } from "@/data/api";
import { modal } from "@/components/overlay/overlay";
import { AtlasIconLogo } from "@/components/icons/logo";
import { Icon } from "@/components/icons/interface";
import { useAuth } from "./auth-provider";
import { GoogleButton } from "./google-button";
const auth = api.organization.user;
type Challenge = {
  token: string;
  methods: {
    _id: string;
    type: string;
    name: string;
  }[];
};
type LoginResult = Awaited<ReturnType<typeof auth.authentication.login.fetch>>;
const errors: Record<string, string> = {
  "invalid-auth": "That email or password wasn’t recognized. Please try again.",
  "conflict": "An account with these details already exists. Try signing in.",
  "invalid-email-or-password": "Enter a valid email, your name, and a password of at least 12 characters.",
  "invalid-google-token": "Google sign-in expired. Please try again.",
  "invalid-mfa": "That code didn’t work. Check your authenticator and try again.",
  "invalid-code": "That code didn’t work. Check your authenticator and try again.",
  "invalid-challenge": "This sign-in attempt expired. Go back and sign in again.",
  "too-many-attempts": "Too many attempts. Please wait a few minutes before trying again.",
  "setup-expired-or-limited": "Setup expired. Go back and start authenticator setup again.",
  "mfa-already-configured": "An authenticator is already set up for this account."
};
function checked<T>(result: T): Exclude<T, {
  failed: string;
}> {
  if (!result || typeof result !== "object" || "error" in result) throw new Error("Atlas couldn’t complete the request. Please try again.");
  if ("failed" in result) throw new Error(errors[String(result.failed)] || "Couldn’t complete this step. Please try again.");
  return result as Exclude<T, {
    failed: string;
  }>;
}
export function AuthModal(t0) {
  const $ = _c(7);
  const {
    mode,
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "min(100%, 29rem)",
      maxHeight: "calc(100svh - 2rem)"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== mode || $[2] !== onClose) {
    t2 = <AuthForm initialMode={mode} onClose={onClose} />;
    $[1] = mode;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onClose || $[5] !== t2) {
    t3 = <modal.div onClose={onClose} modalStyle={t1} className="atlas-auth">{t2}</modal.div>;
    $[4] = onClose;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function AuthForm({
  initialMode,
  onClose
}: {
  initialMode: "login" | "signup";
  onClose: () => void;
}) {
  const {
    refresh
  } = useAuth();
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const working = useRef(false);
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState<"auth" | "mfa" | "security" | "setup">("auth");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [method, setMethod] = useState("");
  const [code, setCode] = useState("");
  const [setup, setSetup] = useState<{
    method: string;
    secret: string;
  } | null>(null);
  const [secured, setSecured] = useState(false);
  const linkToken = useRef<string | null>(null);
  useEffect(() => {
    const dialog = root.current?.closest<HTMLElement>('[role="dialog"]');
    const background = document.querySelector<HTMLElement>("[data-atlas-content]");
    const previous = document.activeElement as HTMLElement | null;
    if (!dialog) return;
    dialog.setAttribute("aria-labelledby", `${id}-title`);
    if (background) background.inert = true;
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const elements = Array.from(dialog.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), iframe, [tabindex="0"]')).filter(element => element.getClientRects().length && !element.closest("[inert]"));
      const first = elements[0],
        last = elements.at(-1);
      if (!first) {
        event.preventDefault();
        root.current?.focus();
        return;
      }
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.removeEventListener("keydown", trapFocus);
      if (background) background.inert = false;
      (previous?.isConnected ? previous : document.querySelector<HTMLElement>(".atlas-topbar .atlas-wordmark"))?.focus();
    };
  }, [id]);
  useEffect(() => {
    root.current?.querySelector<HTMLElement>("[data-auth-focus]")?.focus();
  }, [mode, step]);
  async function run(action: () => Promise<void>) {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Something went wrong. Please try again.");
    } finally {
      working.current = false;
      setBusy(false);
    }
  }
  async function signedIn(recommendSetup = false) {
    const user = await refresh();
    if (!user) throw new Error("Sign-in couldn’t be confirmed. Please try signing in again.");
    setPassword("");
    setCode("");
    if (linkToken.current) {
      const token = linkToken.current;
      linkToken.current = null;
      try {
        const result = checked(await auth.account.authentication.google.link.fetch({
          body: {
            token
          }
        }));
        if (!result.linked) throw new Error();
      } catch {
        setStep("security");
        setNotice("You’re signed in. Google couldn’t be linked. You can continue using your password.");
        return;
      }
    }
    if (recommendSetup) {
      setStep("security");
      setNotice("Your account is ready. Add an authenticator for an extra layer of security, or continue for now.");
    } else onClose();
  }
  async function finishLogin(response: LoginResult, googleToken?: string, recommendSetup_0 = false) {
    const result_0 = checked(response);
    if ("twoStep" in result_0) {
      const methods = result_0.methods.filter(item => item.type === "totp" || item.type === "backup_code");
      if (!methods.length) throw new Error("No supported verification method is available for this account.");
      setChallenge({
        ...result_0,
        methods
      });
      setMethod(methods[0]._id);
      setPassword("");
      setCode("");
      setStep("mfa");
      setNotice("");
    } else if ("linkRequired" in result_0) {
      linkToken.current = googleToken || null;
      setMode("login");
      setEmail(result_0.email);
      setPassword("");
      setNotice("This email already has an Atlas account. Sign in with its password to link Google.");
    } else if ("providerRequired" in result_0) {
      setMode("login");
      setPassword("");
      setNotice("This account uses Google. Choose Continue with Google to sign in.");
    } else if ("loggedIn" in result_0 && result_0.loggedIn) await signedIn(recommendSetup_0);else throw new Error("Sign-in wasn’t completed. Please try again.");
  }
  function submit(event_0: FormEvent<HTMLFormElement>) {
    event_0.preventDefault();
    void run(async () => {
      if (step === "mfa" && challenge) {
        const result_1 = checked(await auth.authentication.verify.fetch({
          body: {
            method,
            code: code.trim(),
            token: challenge.token
          }
        }));
        if (!result_1.loggedIn) throw new Error("Verification wasn’t completed. Please try again.");
        await signedIn();
      } else if (step === "setup" && setup) {
        const result_2 = checked(await auth.account.mfa.totp.verify.fetch({
          body: {
            method: setup.method,
            code: code.trim()
          }
        }));
        if (!result_2.created) throw new Error("Authenticator setup wasn’t completed. Please try again.");
        setSetup(null);
        setSecured(true);
        setStep("security");
        setNotice("Your authenticator is ready. You’ll use a code from it when you sign in.");
      } else if (step === "auth") {
        let created = false;
        if (mode === "signup") {
          const result_3 = checked(await auth.signup.start.fetch({
            body: {
              displayName: name.trim(),
              email: email.trim(),
              password
            }
          }));
          if (result_3.providerRequired) {
            setMode("login");
            setPassword("");
            setNotice("This email already uses Google. Choose Continue with Google to sign in.");
            return;
          }
          if (!result_3.created || !result_3.loginRequired) throw new Error("Account creation wasn’t completed. Please try again.");
          created = true;
          setMode("login");
          setNotice("Your account was created. Finishing sign-in…");
        }
        await finishLogin(await auth.authentication.login.fetch({
          body: {
            identity: email.trim(),
            provider: {
              type: "password",
              password
            }
          }
        }), undefined, created);
      }
    });
  }
  function googleSignIn(token_0: string) {
    void run(async () => {
      if (mode === "login") {
        await finishLogin(await auth.authentication.login.fetch({
          body: {
            provider: {
              type: "google",
              token: token_0
            }
          }
        }), token_0);
        return;
      }
      const result_4 = checked(await auth.signup.start.fetch({
        body: {
          googleToken: token_0
        }
      }));
      if (result_4.linkRequired) {
        linkToken.current = token_0;
        setMode("login");
        setEmail(result_4.email || "");
        setNotice("This email already has an Atlas account. Sign in with its password to link Google.");
      } else if (result_4.loggedIn) await signedIn(Boolean(result_4.mfaSetupRecommended));else if (result_4.loginRequired) await finishLogin(await auth.authentication.login.fetch({
        body: {
          provider: {
            type: "google",
            token: token_0
          }
        }
      }), token_0);else throw new Error("Google sign-in wasn’t completed. Please try again.");
    });
  }
  const title = step === "mfa" ? "Verify sign-in" : step === "setup" ? "Connect an authenticator" : step === "security" ? "Account ready" : mode === "signup" ? "Create an account" : "Sign in to Atlas";
  return <div ref={root} tabIndex={-1}>
        <div className="atlas-auth-brand"><AtlasIconLogo /><span>atlas</span></div>
        <h2 id={`${id}-title`}>{title}</h2>
        {notice && <p className="atlas-form-notice" role="status">{notice}</p>}
        {error && <p id={`${id}-error`} className="atlas-form-error" role="alert">{error}</p>}
        {step === "security" ? <div className="atlas-auth-security">
            {!secured && <button data-auth-focus className="atlas-button atlas-button-secondary" disabled={busy} onClick={() => void run(async () => {
        const result_5 = checked(await auth.account.mfa.totp.start.fetch({
          body: {
            name: "Authenticator"
          }
        }));
        if (!result_5.method || !result_5.secret) throw new Error("Couldn’t start authenticator setup. Please try again.");
        setSetup({
          method: result_5.method,
          secret: result_5.secret
        });
        setCode("");
        setNotice("");
        setStep("setup");
      })}><Icon name="lock" />{busy ? "Starting setup…" : "Set up an authenticator"}</button>}
            <button data-auth-focus className="atlas-button atlas-button-primary" disabled={busy} onClick={onClose}>Continue to Atlas <Icon name="arrow" /></button>
        </div> : <form onSubmit={submit} aria-busy={busy} aria-describedby={error ? `${id}-error` : undefined}>
            <fieldset disabled={busy} className="atlas-auth-fields">
                {step === "auth" ? <>
                    {process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID && <div inert={busy}><GoogleButton onCredential={googleSignIn} /></div>}
                    {mode === "signup" && <label htmlFor={`${id}-name`}>Name<input data-auth-focus id={`${id}-name`} name="name" autoComplete="name" value={name} onChange={event_1 => setName(event_1.target.value)} required maxLength={100} /></label>}
                    <label htmlFor={`${id}-email`}>Email<input data-auth-focus={mode === "login" ? true : undefined} id={`${id}-email`} name="email" type="email" autoComplete="username" value={email} onChange={event_2 => {
              setEmail(event_2.target.value);
              linkToken.current = null;
            }} required /></label>
                    <label htmlFor={`${id}-password`}><span className="atlas-auth-field-label">Password{mode === "signup" && <small>12+ characters</small>}</span><span className="atlas-password-input"><input id={`${id}-password`} name="password" type={visible ? "text" : "password"} autoComplete={mode === "signup" ? "new-password" : "current-password"} value={password} onChange={event_3 => setPassword(event_3.target.value)} required minLength={mode === "signup" ? 12 : undefined} /><button type="button" className="atlas-icon-button" aria-label={visible ? "Hide password" : "Show password"} aria-pressed={visible} onClick={() => setVisible(!visible)}><Icon name="eye" /></button></span></label>
                </> : <>
                    {step === "mfa" && challenge && challenge.methods.length > 1 && <label htmlFor={`${id}-method`}>Verification method<select id={`${id}-method`} value={method} onChange={event_4 => {
              setMethod(event_4.target.value);
              setCode("");
              setError("");
            }}>{challenge.methods.map(item_0 => <option key={item_0._id} value={item_0._id}>{item_0.name || item_0.type}</option>)}</select></label>}
                    {step === "setup" && setup && <><label htmlFor={`${id}-secret`}>Setup key<input id={`${id}-secret`} className="atlas-setup-key" value={setup.secret} readOnly aria-describedby={`${id}-key-help`} onFocus={event_5 => event_5.target.select()} /></label><p id={`${id}-key-help`} className="atlas-form-note">Account: {email || "Atlas"} · Time-based (TOTP)</p></>}
                    <label htmlFor={`${id}-code`}>Verification code<input data-auth-focus id={`${id}-code`} name="code" inputMode={challenge?.methods.find(item_1 => item_1._id === method)?.type === "backup_code" ? "text" : "numeric"} autoComplete="one-time-code" value={code} onChange={event_6 => setCode(event_6.target.value)} required placeholder="Enter your code" /></label>
                </>}
                <button type="submit" className="atlas-button atlas-button-primary atlas-auth-submit">{busy ? "Please wait…" : step === "mfa" ? "Verify and sign in" : step === "setup" ? "Verify authenticator" : mode === "signup" ? "Create account" : linkToken.current ? "Sign in and link Google" : "Sign in"}</button>
            </fieldset>
            {step === "auth" ? <p className="atlas-auth-switch">{mode === "signup" ? "Already have an account?" : "New to Atlas?"} <button type="button" disabled={busy} onClick={() => {
          setMode(mode === "signup" ? "login" : "signup");
          setPassword("");
          setVisible(false);
          setError("");
          setNotice("");
          linkToken.current = null;
        }}>{mode === "signup" ? "Sign in" : "Create an account"}</button></p> : <button type="button" className="atlas-auth-back" disabled={busy} onClick={() => {
        setStep(step === "setup" ? "security" : "auth");
        setSetup(null);
        setChallenge(null);
        setCode("");
        setError("");
        setNotice("");
      }}>Back</button>}
        </form>}
    </div>;
}